import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExternalLink, Download, AlertCircle, Loader2, FileText, Globe } from 'lucide-react';

interface HtmlDocumentViewerProps {
  documentData: any;
  downloadUrl: string;
  fileName?: string;
  className?: string;
  height?: string;
}

export const HtmlDocumentViewer: React.FC<HtmlDocumentViewerProps> = ({
  documentData,
  downloadUrl,
  fileName = 'Document',
  className = '',
  height = '700px'
}) => {
  const [viewMode, setViewMode] = useState<'html' | 'office'>('office');
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    if (viewMode === 'office') {
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(downloadUrl)}`;
      window.open(officeUrl, '_blank');
    } else {
      const htmlContent = generateHtmlContent(documentData);
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      }
    }
  };

  const generateOfficeOnlineUrl = () => {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(downloadUrl)}`;
  };

  const generateHtmlContent = (data: any) => {
    const { personalInfo, summary, experience, education, skills } = data;
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${fileName}</title>
    <style>
        body {
            font-family: 'Calibri', Arial, sans-serif;
            margin: 0;
            padding: 0;
            background: #f0f0f0;
            color: #333;
            line-height: 1.4;
        }
        
        .document-container {
            width: 8.5in;
            min-height: 11in;
            padding: 1in;
            margin: 0.5in auto;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            position: relative;
        }
        
        .header {
            text-align: center;
            margin-bottom: 30px;
            page-break-inside: avoid;
        }
        .name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 8px;
            color: #2c3e50;
        }
        .contact {
            font-size: 12px;
            color: #555;
            margin-bottom: 20px;
        }
        .section-title {
            font-size: 14px;
            font-weight: bold;
            text-transform: uppercase;
            color: #2c3e50;
            border-bottom: 1px solid #2c3e50;
            margin: 20px 0 10px 0;
            padding-bottom: 2px;
            page-break-after: avoid;
        }
        .job-title {
            font-weight: bold;
            margin-bottom: 3px;
        }
        .job-details {
            font-size: 11px;
            color: #666;
            margin-bottom: 8px;
        }
        .responsibility {
            margin: 3px 0 3px 20px;
            font-size: 11px;
        }
        .education-item {
            margin-bottom: 10px;
            page-break-inside: avoid;
        }
        .experience-item {
            margin-bottom: 15px;
            page-break-inside: avoid;
        }
        .degree {
            font-weight: bold;
            margin-bottom: 3px;
        }
        .school-details {
            font-size: 11px;
            color: #666;
        }
        .summary {
            margin-bottom: 20px;
            text-align: justify;
            font-size: 11px;
            page-break-inside: avoid;
        }
        .skills {
            font-size: 11px;
            page-break-inside: avoid;
        }
        
        @media print {
            body { 
                margin: 0; 
                padding: 0; 
                background: white;
            }
            .document-container {
                box-shadow: none;
                margin: 0;
                width: 100%;
                min-height: auto;
            }
            .page-break-before { page-break-before: always; }
            .page-break-after { page-break-after: always; }
            .page-break-inside-avoid { page-break-inside: avoid; }
        }
        
        @page {
            size: letter;
            margin: 1in;
        }
    </style>
</head>
<body>
    <div class="document-container">
        ${personalInfo ? `
        <div class="header">
            <div class="name">${escapeHtml(personalInfo.name)}</div>
            <div class="contact">
                ${escapeHtml(personalInfo.email)} • ${escapeHtml(personalInfo.phone)} • ${escapeHtml(personalInfo.location)}
            </div>
        </div>
        ` : ''}

        ${summary ? `
        <div class="section-title">Professional Summary</div>
        <div class="summary">${escapeHtml(summary)}</div>
        ` : ''}

        ${experience && experience.length > 0 ? `
        <div class="section-title">Professional Experience</div>
        ${experience.map((exp: any) => `
        <div class="experience-item">
            <div class="job-title">${escapeHtml(exp.title)}</div>
            <div class="job-details">${escapeHtml(exp.company)}, ${escapeHtml(exp.location)} | ${escapeHtml(exp.startDate)} - ${escapeHtml(exp.endDate)}</div>
            ${exp.responsibilities.map((resp: string) => `
            <div class="responsibility">• ${escapeHtml(resp)}</div>
            `).join('')}
        </div>
        `).join('')}
        ` : ''}

        ${education && education.length > 0 ? `
        <div class="section-title">Education</div>
        ${education.map((edu: any) => `
        <div class="education-item">
            <div class="degree">${escapeHtml(edu.degree)}</div>
            <div class="school-details">${escapeHtml(edu.school)}, ${escapeHtml(edu.location)} | ${escapeHtml(edu.graduationDate)}</div>
        </div>
        `).join('')}
        ` : ''}

        ${skills && skills.length > 0 ? `
        <div class="section-title">Skills</div>
        <div class="skills">${escapeHtml(skills.join(', '))}</div>
        ` : ''}
    </div>
</body>
</html>`;
  };

  const renderPaginatedContent = () => {
    const { personalInfo, summary, experience, education, skills } = documentData;
    
    // Calculate content sections
    const sections = [];
    
    // Header section
    if (personalInfo) {
      sections.push({
        type: 'header',
        content: (
          <div className="text-center mb-8">
            <div className="text-2xl font-bold mb-2 text-slate-800">
              {personalInfo.name}
            </div>
            <div className="text-sm text-slate-600 mb-5">
              {personalInfo.email} • {personalInfo.phone} • {personalInfo.location}
            </div>
          </div>
        ),
        height: 120 // approximate height in pixels
      });
    }
    
    // Summary section
    if (summary) {
      sections.push({
        type: 'summary',
        content: (
          <div className="mb-6">
            <div className="text-sm font-bold uppercase text-slate-800 border-b border-slate-800 mb-3 pb-1">
              Professional Summary
            </div>
            <div className="text-xs text-justify">
              {summary}
            </div>
          </div>
        ),
        height: Math.max(80, Math.ceil(summary.length / 80) * 20) // estimate based on text length
      });
    }
    
    // Experience section
    if (experience && experience.length > 0) {
      sections.push({
        type: 'experience-header',
        content: (
          <div className="text-sm font-bold uppercase text-slate-800 border-b border-slate-800 mb-3 pb-1">
            Professional Experience
          </div>
        ),
        height: 30
      });
      
      experience.forEach((exp: any, index: number) => {
        sections.push({
          type: 'experience-item',
          content: (
            <div className="mb-4">
              <div className="font-bold text-sm mb-1">{exp.title}</div>
              <div className="text-xs text-slate-600 mb-2">
                {exp.company}, {exp.location} | {exp.startDate} - {exp.endDate}
              </div>
              {exp.responsibilities.map((resp: string, respIndex: number) => (
                <div key={respIndex} className="text-xs ml-5 mb-1">
                  • {resp}
                </div>
              ))}
            </div>
          ),
          height: 60 + (exp.responsibilities.length * 20)
        });
      });
    }
    
    // Education section
    if (education && education.length > 0) {
      sections.push({
        type: 'education-header',
        content: (
          <div className="text-sm font-bold uppercase text-slate-800 border-b border-slate-800 mb-3 pb-1">
            Education
          </div>
        ),
        height: 30
      });
      
      education.forEach((edu: any, index: number) => {
        sections.push({
          type: 'education-item',
          content: (
            <div className="mb-3">
              <div className="font-bold text-sm mb-1">{edu.degree}</div>
              <div className="text-xs text-slate-600">
                {edu.school}, {edu.location} | {edu.graduationDate}
              </div>
            </div>
          ),
          height: 50
        });
      });
    }
    
    // Skills section
    if (skills && skills.length > 0) {
      sections.push({
        type: 'skills',
        content: (
          <div>
            <div className="text-sm font-bold uppercase text-slate-800 border-b border-slate-800 mb-3 pb-1">
              Skills
            </div>
            <div className="text-xs">
              {skills.join(', ')}
            </div>
          </div>
        ),
        height: 60
      });
    }
    
    // Distribute sections across pages
    const pages = [];
    let currentPage = [];
    let currentPageHeight = 0;
    const maxPageHeight = 648; // 9 inches in pixels (72px per inch)
    
    sections.forEach((section, index) => {
      if (currentPageHeight + section.height > maxPageHeight && currentPage.length > 0) {
        // Start new page
        pages.push([...currentPage]);
        currentPage = [section];
        currentPageHeight = section.height;
      } else {
        currentPage.push(section);
        currentPageHeight += section.height;
      }
    });
    
    // Add the last page if it has content
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    // Render pages
    return (
      <div>
        {pages.map((page, pageIndex) => (
          <div key={pageIndex}>
            <div className="document-page">
              <div className="content-area">
                {page.map((section, sectionIndex) => (
                  <div key={sectionIndex}>
                    {section.content}
                  </div>
                ))}
              </div>
              <div className="page-number">Page {pageIndex + 1}</div>
            </div>
            {pageIndex < pages.length - 1 && (
              <div className="page-break-indicator"></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const escapeHtml = (unsafe: string): string => {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  return (
    <div className={className}>
      <Card>
        {/* Header with controls */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-semibold">{fileName}</h3>
            <p className="text-sm text-muted-foreground">
              {viewMode === 'office' ? 'Microsoft Office Online View' : 'Clean Document Preview'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={openInNewTab}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Full
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={handleDownload}
            >
              <Download className="w-4 h-4 mr-2" />
              Download .docx
            </Button>
          </div>
        </div>

        {/* View Mode Tabs */}
        <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'html' | 'office')} className="w-full">
          <TabsList className="w-full justify-start border-b rounded-none h-12 px-4">
            <TabsTrigger value="office" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Office Online
            </TabsTrigger>
            <TabsTrigger value="html" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              HTML Preview
            </TabsTrigger>
          </TabsList>

          <TabsContent value="office" className="m-0">
            {/* Office Online Embed */}
            <div className="relative">
              <iframe
                src={generateOfficeOnlineUrl()}
                width="100%"
                height={height}
                frameBorder="0"
                title={`${fileName} - Office Online`}
                className="border-0"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
              />
              
              {/* Loading overlay */}
              <div className="absolute inset-0 bg-gray-50 flex items-center justify-center" 
                   style={{ zIndex: -1 }}>
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
                  <p className="text-sm text-gray-600">Loading Microsoft Office Online...</p>
                </div>
              </div>
            </div>
            
            {/* Office Online Info */}
            <div className="p-4 border-t bg-blue-50">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <div className="text-sm">
                  <p className="font-medium text-blue-800 mb-1">Microsoft Office Online</p>
                  <p className="text-blue-700">
                    Pixel-perfect rendering using Microsoft's official Word viewer. 
                    This is exactly how your document appears in Microsoft Word.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="html" className="m-0">

            {/* Document viewer */}
            <div 
              className="bg-gray-100 p-4 rounded-lg overflow-hidden"
              style={{ 
                minHeight: height,
                fontFamily: 'Calibri, Arial, sans-serif'
              }}
            >
              {/* Add CSS for proper page visualization */}
              <style>
                {`
                  .document-page {
                    width: 612px;  /* 8.5 inches at 72 DPI */
                    height: 792px; /* 11 inches at 72 DPI */
                    padding: 72px; /* 1 inch at 72 DPI */
                    margin: 0 auto 20px auto;
                    background: white;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    position: relative;
                    overflow: hidden;
                    font-family: 'Calibri', Arial, sans-serif;
                    line-height: 1.4;
                    page-break-after: always;
                    transform-origin: top center;
                    scale: 0.8; /* Scale down for better viewport fit */
                  }
                  
                  .page-number {
                    position: absolute;
                    bottom: 36px; /* 0.5 inch at 72 DPI */
                    right: 72px;  /* 1 inch at 72 DPI */
                    font-size: 10px;
                    color: #999;
                  }
                  
                  .content-area {
                    height: 648px; /* 9 inches at 72 DPI (11" - 2" margins) */
                    overflow: hidden;
                  }
                  
                  @media print {
                    .document-page {
                      box-shadow: none;
                      margin: 0;
                      page-break-after: always;
                    }
                    .page-break-indicator {
                      display: none;
                    }
                  }
                  
                  .page-break-indicator {
                    height: 2px;
                    background: #e0e0e0;
                    margin: 10px 0;
                    position: relative;
                  }
                  
                  .page-break-indicator::after {
                    content: "Page Break";
                    position: absolute;
                    right: 0;
                    top: -15px;
                    font-size: 10px;
                    color: #999;
                    background: white;
                    padding: 0 5px;
                  }
                `}
              </style>
              
              {/* Render content with actual page visualization */}
              {renderPaginatedContent()}
            </div>

            {/* Info section */}
            <div className="p-4 border-t bg-green-50">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <div className="text-sm">
                  <p className="font-medium text-green-800 mb-1">Clean Preview Experience</p>
                  <p className="text-green-700">
                    No scrollbars or Microsoft interface - just your document content. 
                    Download the .docx file for full editing in Microsoft Word.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};